import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import { useActingClientAuthorizedCheck } from 'components/patient/useActingClientAuthorizedCheck';
import CircularProgress from 'components/shared/CircularProgress';
import { workflowQueryKeys } from 'services/api/workflows';

import { TASKS_ROUTES } from './constants';

type Props = {
  children: React.ReactNode;
};

// WorkflowScopeGate's sibling for the tasks deep link (OLIO-3107): the workflow arrives
// as `?task=<id>` rather than a path segment, because the tasks route cannot grow a
// sibling route without remounting the page (see App.tsx — that teardown closes users'
// portal tabs). Scopes the acting client BEFORE ProtectedRoute reads it, same as the
// show-page gate (OLIO-2960).
//
// Only the task the URL ARRIVED with is checked — captured once on mount. The page
// syncs the param as slideouts open and close, and re-running the check on every open
// would swap the page for a spinner mid-session (a remount, the exact thing the query
// param exists to avoid). Anything opened from within the page is already scope-safe:
// the lists only offer the acting client's work.
export default function TasksDeepLinkScopeGate({ children }: Props) {
  const [searchParams] = useSearchParams();
  const [initialTask] = useState(() => searchParams.get('task'));
  const queryClient = useQueryClient();

  // Lists cached under the previous acting client must not survive the switch.
  const handleClientSwitched = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: workflowQueryKeys.base });
  }, [queryClient]);

  const authorizationChecked = useActingClientAuthorizedCheck(initialTask ?? undefined, {
    resourceType: 'workflow',
    notFoundText: 'Referral not found',
    fallbackPath: TASKS_ROUTES.BASE,
    onClientSwitched: handleClientSwitched,
  });

  if (initialTask && !authorizationChecked) {
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  }

  return <>{children}</>;
}

const Centered = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;
